import * as React from "react";

import LabelImportantIcon from "@mui/icons-material/LabelImportant";
import { useNavigate } from "react-router-dom";
import { Message } from "../../Interface/Message";
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppHelper from "../../Helper/AppHelper";
import Loader from "../Loader";
import AlertBox from "../CommonComponent/AlertBox";
import { AppSettings } from "../../AppSettings";
import {
  GridEventListener,
  GridRowModel,
  GridRowModesModel,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModes,
  GridActionsCellItem,
  GridColDef, DataGrid,
  GridToolbarContainer
} from "@mui/x-data-grid";
import { FourkiteRuleElements, FourkiteRuleInfo } from "../../Interface/FourkiteRuleElements";
import { randomId } from "@mui/x-data-grid-generator";
import { forkJoin } from "rxjs";
import { isNumber } from "@mui/x-data-grid/internals";

function EditToolbar(props: any) {
  const { setRows, setRowModesModel, headerList } = props;

  const handleClick = () => {
    const id = randomId();
    const newRow: any = {
      id: id,
      isNew: true,
    };
    headerList.forEach((header: string) => {
      newRow[header.toLowerCase()] = null;
    });
    setRows((oldRows: any) => [newRow, ...oldRows]);
    setRowModesModel((oldModel: any) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: headerList[0] },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        <b>Add record</b>
      </Button>
    </GridToolbarContainer>
  );
}

export default function AddFourKitesRule() {
  const [rows, setRows] = React.useState<GridRowModel[]>([]);
  const [deletedRowIds, setDeletedRowIds] = React.useState<number[]>([]);
  const [headerList, setHeaderList] = React.useState<string[]>([]);
  const [FourKitesRule, setFourKitesRuleList] = React.useState<FourkiteRuleInfo>({
    id: 0,
    name: "",
    description: "",
    isActive: true
  });
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = headerList.map((header) => ({
    field: header.toLowerCase(),
    headerName: header,
    width: 180,
    editable: true,
  }));

  const handleProcessRowUpdateError = (error: any) => {
    setRowModesModel(rowModesModel)
  };

  columns.push({
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    width: 100,
    cellClassName: 'actions',
    getActions: ({ id }: any) => {
      const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

      if (isInEditMode) {
        return [
          <GridActionsCellItem
            icon={<SaveIcon />}
            label="Save"
            sx={{
              color: 'primary.main',
            }}
            onClick={handleSaveClick(id)} />,
          <GridActionsCellItem
            icon={<CancelIcon />}
            label="Cancel"
            className="textPrimary"
            onClick={handleCancelClick(id)}
            color="inherit" />,
        ];
      }

      return [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          className="textPrimary"
          onClick={handleEditClick(id)}
          color="inherit" />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={handleDeleteClick(id)}
          color="inherit" />,
      ];
    },
  });

  const [isChecked, setIsChecked] = React.useState(true);

  function getFourKiteRulesAndRules(idParam: string | null) {
    setOpenloader(true);

    const fourKiteRules$ = fetchFourKiteRules(idParam);
    const rules$ = fetchRules(idParam);

    forkJoin({ fourKiteRules$, rules$ }).subscribe({
      next: (response) => {
        const fourKiteRulesData = response.fourKiteRules$;
        const rulesData = response.rules$;
        setFourKitesRuleList(fourKiteRulesData);
        setIsChecked((fourKiteRulesData.id === 0) ? true : fourKiteRulesData.isActive)
        setHeaderList(rulesData.headers);
        const generatedRows: GridRowModel[] = rulesData.rules.map((rule: FourkiteRuleElements) => {
          const row: GridRowModel = {
            id: rule.id,
          };
          rulesData.headers.forEach((header: string) => {
            row[header.toLowerCase()] = rule.dictionaryProperty[header];
          });
          return row;
        });
        setRows(generatedRows);
      },
      error: (err) => {
        if (err.message === "Failed to fetch") {
          navigate("/SessionExpired");
        } else {
          const msg = { Text: err.message, Type: "Error" };
          setAlertBoxmsg(msg);
        }
      },
      complete: () => {
        setOpenloader(false);
      }
    });
  }

  function fetchFourKiteRules(idParam: string | null) {
    const apiUrl = `${AppSettings.API_URL}Fourkites/GetFourKitesRuleById?id=${idParam ?? 0}`;
    return fetch(apiUrl, AppHelper.requestOptions("POST")).then((response) => {
      if (!response.ok) {
        if (response.status == 401) navigate("/SessionExpired");
        else
          throw new Error(
            `API Error, This is an HTTP error: The status is ${response.status}`
          );
      }
      return response.json();
    });
  }

  function fetchRules(idParam: string | null) {
    const apiUrl = `${AppSettings.API_URL}Fourkites/GetAllFourKitesRuleElement?id=${idParam ?? 0}`;
    return fetch(apiUrl, AppHelper.requestOptions("POST")).then((response) => {
      if (!response.ok) {
        if (response.status == 401) navigate("/SessionExpired");
        else
          throw new Error(
            `API Error, This is an HTTP error: The status is ${response.status}`
          );
      }
      return response.json();
    });
  }

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    if (isNumber(id)) {
      setDeletedRowIds([...deletedRowIds, id]);
    }
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const [pageName, setPageName] = React.useState("Add Service");
  const [openLoader, setOpenloader] = React.useState(false);

  const [formValidation, setFormValidation] = React.useState<{
    [key: string]: boolean;
  }>({
    name: false,
    description: false,
    dictionaryProperty: false
  });

  const [alertBoxmsg, setAlertBoxmsg] = React.useState<Message>({
    Type: "",
    Text: "",
  });

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | (Event & { target: { value: string; id: string } })
  ) => {
    const { id, value } = e.target as HTMLInputElement | HTMLTextAreaElement;
    setFourKitesRuleList({ ...FourKitesRule, [id]: value });
    isValidForm(id, value);
  };

  const handleCheckboxChange = (event: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) => {
    setIsChecked(event.target.checked);
  };

  const isValidForm = (id: string, value: string) => {
    let valid = true;

    switch (id) {
      case 'description':
        if (AppHelper.isNullorEmpty(value)) {
          valid = false;
        }
        break;
      case 'name':
        if (AppHelper.isNullorEmpty(value)) {
          valid = false;
        }
        break;
      case 'dictionaryProperty':
        if (AppHelper.isNullorEmpty(value)) {
          valid = false;
        }
        break;
      default:
        break;
    }

    setFormValidation((prevValidation) => ({
      ...prevValidation,
      [id]: !valid,
    }));
  };



  const handleSubmit = () => {
    let msg: Message = { Text: "", Type: "" };
    setAlertBoxmsg(msg);
    const checkDublicate: string[] = [];
    let dublicateExsists: boolean = false;
    const emptyEntries = rows.some(entry => {
      const keys = Object.keys(entry);
      for (const key of keys) {
        if (key !== 'id' && key !== 'isNew' && !AppHelper.isNullorEmpty(entry[key])) {
          return false;
        }
      }
      return true;
    });

    if (emptyEntries) {
      msg = { Text: "Empty Row(s) OR Unsaved Data in Rule Element Table", Type: "Error" };
      setAlertBoxmsg(msg);
      return;
    }

    const elementsArray: FourkiteRuleElements[] = [];
    rows.forEach(row => {
      const { id: rowId, isNew: isNew, ...dataToSend } = row;
      const dictionaryProperty: { [key: string]: string } = {};
      let dublicateCheckerEntry = '';
      for (const key in dataToSend) {
        if (Object.prototype.hasOwnProperty.call(dataToSend, key)) {
          dictionaryProperty[key] = dataToSend[key];
          dublicateCheckerEntry = dublicateCheckerEntry + dataToSend[key];
        }
      }
      if (checkDublicate.includes(dublicateCheckerEntry)) {
        dublicateExsists = true;
        msg = { Text: " Duplicate Rule(s) or Rule is already present.", Type: "Error" };
        setAlertBoxmsg(msg);
      } else {
        checkDublicate.push(dublicateCheckerEntry);
      }
      elementsArray.push({ id: !!isNaN(rowId) ? 0 : rowId, dictionaryProperty: dictionaryProperty })
    })

    if (dublicateExsists) {
      return;
    }

    if (!Object.keys(formValidation).some((key) => formValidation[key]) && !emptyEntries && !dublicateExsists) {
      setOpenloader(true);
      const formData: FourkiteRuleInfo = {
        id: FourKitesRule.id ?? 0,
        name: FourKitesRule.name ?? "",
        description: FourKitesRule.description ?? "",
        isActive: isChecked ?? "",
        fourkiteRuleElements: elementsArray ?? [],
        deletedRows: deletedRowIds
      };
      const requestOptions = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + AppHelper.getUser().token,
        },
        body: JSON.stringify(formData),
      };

      let apiUrl = AppSettings.API_URL + "Fourkites/SaveRuleAndElements";

      if (FourKitesRule.id > 0) {
        apiUrl = AppSettings.API_URL + "Fourkites/UpdateRuleAndElements";
      }

      fetch(apiUrl, requestOptions)
        .then((response) => {
          if (!response.ok) {
            if (response.status == 401) navigate("/SessionExpired");
            else
              throw new Error(
                `API Error, This is an HTTP error: The status is ${response.status}`
              );
          }
          return response.json();
        })
        .then((data) => {
          if (data) {
            if (data.successes.length > 0) {
              let redirectPath = "";
              if (FourKitesRule.id > 0) {
                redirectPath = "/Fourkites?msg=Update_Successful"
              } else {
                redirectPath = "/Fourkites?msg=Create_Successful"
              }
              navigate(redirectPath);
            } else {
              msg = { Text: data.errors[0], Type: "Error" };
              setAlertBoxmsg(msg);
            }
          }
          setOpenloader(false);
        })
        .catch((err) => {
          if (err.message === "Failed to fetch") {
            navigate("/SessionExpired");
          } else {
            msg = { Text: err.message, Type: "Error" };
            setAlertBoxmsg(msg);
          }
        })
        .finally(() => {
          setOpenloader(false);
        });
    }
  };

  const navigate = useNavigate();

  const effectCount = React.useRef(true);

  React.useEffect(() => {
    //AppHelper.isSuperAdminUser();
    if (effectCount.current) {
      effectCount.current = false;
    }
    let query = AppHelper.getQueryValue().get("id");
    getFourKiteRulesAndRules(query);

    // if (effectCount.current) {
    //   effectCount.current = false;
    // }

    if (query === null) {
      setPageName("Add FourKites Rule");
      return;
    }
    if (query !== undefined || query !== "" || query !== null) {
      setPageName("Update FourKites Rule");
      setOpenloader(true);
      getFourKiteRulesAndRules(query);
    }
  }, []);

  return (
    <>
      <div className="page-container">
        <div className="add-details">
          <Loader value={openLoader}></Loader>
          <Container component="main" maxWidth="md">
            <CssBaseline />
            <Box
              sx={{
                marginTop: 3,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                component="h1"
                variant="h5"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <ManageAccountsIcon
                  sx={{ fontSize: 30, marginRight: 1 }}
                ></ManageAccountsIcon>{" "}
                <b>{pageName}</b>
              </Typography>

              {alertBoxmsg.Text !== "" && (
                <AlertBox value={alertBoxmsg}></AlertBox>
              )}

              <Box component="form" noValidate sx={{ mt: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {" "}
                    <TextField
                      id="name"
                      label="Name"
                      multiline
                      fullWidth
                      maxRows={4}
                      value={FourKitesRule?.name}
                      onChange={(e) => handleInputChange(e)}
                      error={formValidation.name}
                      helperText={
                        formValidation.name ? "Error : Field is required" : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {" "}
                    <TextField
                      id="description"
                      label="Description"
                      fullWidth
                      multiline
                      rows={1}
                      value={FourKitesRule?.description}
                      onChange={(e) => handleInputChange(e)}
                      error={formValidation.description}
                      helperText={
                        formValidation.description
                          ? "Error : Field is required"
                          : ""
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        height: 300,
                        width: '100%',
                        '& .actions': {
                          color: 'text.secondary',
                        },
                        '& .textPrimary': {
                          color: 'text.primary',
                        },
                      }}
                    >
                      <DataGrid
                        rows={rows}
                        columns={columns}
                        editMode="row"
                        rowModesModel={rowModesModel}
                        onRowModesModelChange={handleRowModesModelChange}
                        onRowEditStop={handleRowEditStop}
                        processRowUpdate={processRowUpdate}
                        onProcessRowUpdateError={handleProcessRowUpdateError}
                        slots={{ toolbar: EditToolbar }}
                        slotProps={{
                          toolbar: { setRows, setRowModesModel, headerList },
                        }}

                      />

                    </Box>
                  </Grid>
                </Grid>
                <Stack spacing={2} padding={2} direction="row">
                  <FormControlLabel
                    className="chekcBox"
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        name="isActive"
                        id="isActive"
                      />
                    }
                    label="Enabled"
                  />
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<LabelImportantIcon />}
                    onClick={() => handleSubmit()}
                  >
                    Submit
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate("/Fourkites")}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Box>
            </Box>
          </Container>
        </div>{" "}
        <div className="blank-div"></div>
      </div>
    </>
  );
}
